import React, { useRef, useState, useEffect } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import { IEcommerceQuestionSchema, EcommerceResponse } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import useOnClickOutside from '@aytm/lib/hooks/useOnClickOutside'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './AddToCartSidebar.module.scss'

export interface IAddToCartSidebarProps {
  index: number
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse['answers']
  deliveryDates: string[]
  findability: boolean
  onSidebarHide: () => void
  onAddToCart: (qnt: number, index: number, dropdown: boolean) => void
  onCartOpen: () => void
  onItemSelect: (index: number) => void
}

const AddToCartSidebar = ({
  index,
  schema,
  response,
  deliveryDates,
  findability,
  onSidebarHide,
  onAddToCart,
  onCartOpen,
  onItemSelect
}: IAddToCartSidebarProps) => {
  const { t, languageTag } = useSurveyLocale()
  const [show, setShow] = useState<boolean>()
  const sidebarRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(sidebarRef, () => onSidebarHide())
  const answer = schema.answers[index]
  const isDiscounted = answer.discounted_price > 0 && answer.price > answer.discounted_price

  useEffect(() => {
    setShow(true)
  }, [])

  return (
    <div className={style.layout}>
      <div className={`${style.sidebar} ${show ? style.sidebarShow : ''}`} ref={sidebarRef}>
        <div className={style.head}>
          <div className={style.title}>
            {response[index].v > 0 ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="Checkmark">
                  <path fill="#00A200" d="m20.53 7.53-1.06-1.06L10 15.94l-4.47-4.47-1.06 1.06L10 18.06z" />
                </svg>
                <span>{t('questions', 'ec', 'target', 'added')}</span>
              </>
            ) : (
              <span>{t('questions', 'ec', 'target', 'choose')}</span>
            )}
          </div>
          <button className={style.close} onClick={onSidebarHide}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="XMark">
              <path
                fill="#333"
                d="m12 10.94 6.54-6.54 1.06 1.06L13.06 12l6.54 6.54-1.06 1.06L12 13.06 5.46 19.6 4.4 18.54 10.94 12 4.4 5.46 5.46 4.4z"
              />
            </svg>
          </button>
        </div>
        <div className={style.content}>
          <div className={style.product}>
            {answer.image ? (
              <img
                src={getMediumAssetPath(answer.image)}
                alt={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
              />
            ) : (
              <div className={style.noImage} />
            )}
            <div className={style.info}>
              <div
                className={style.text}
                dangerouslySetInnerHTML={{ __html: answer.text }}
                onClick={() => {
                  onSidebarHide()
                  onItemSelect(index)
                }}
              />
              <div
                className={style.caption}
                dangerouslySetInnerHTML={{
                  __html: answer.sub_title || ''
                }}
              />
              <div className={style.prices}>
                {isDiscounted && (
                  <>
                    <div className={style.discount}>
                      <span
                        className={style.nowPrice}
                        dangerouslySetInnerHTML={{
                          __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                        }}
                      ></span>
                      {answer.price > 0 && (
                        <>
                          <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                          <span
                            className={`${style.lastPrice}`}
                            dangerouslySetInnerHTML={{
                              __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                            }}
                          />
                        </>
                      )}
                      {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                    </div>
                    <div className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</div>
                  </>
                )}
                {!isDiscounted && answer.price && (
                  <>
                    <span
                      className={style.defaultPrice}
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                      }}
                    />
                    {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={style.shipping}>
            <strong>{t('questions', 'ec', 'target', 'shipTo')}</strong>
            <EmptyLink color="#CC0000">
              <span className={style.edit}>{t('questions', 'ec', 'target', 'edit')}</span>
            </EmptyLink>
          </div>
          <div className={style.arrives}>
            {t<(percentage: string) => string>('questions', 'ec', 'target', 'arrivesBy')(deliveryDates[index])}
          </div>
          <div className={style.shipsFree}>{t('questions', 'ec', 'target', 'shipsFree')}</div>
          {response[index].v > 0 && !findability ? (
            <>
              <button className={style.redButton} onClick={onSidebarHide}>
                {t('questions', 'ec', 'target', 'continue')}
              </button>
              <button
                className={style.whiteButton}
                onClick={() => {
                  onSidebarHide()
                  onCartOpen()
                }}
              >
                {t('questions', 'ec', 'target', 'view')}
              </button>
            </>
          ) : (
            <button
              disabled={1 === response[index].v && findability}
              className={style.redButton}
              onClick={() => onAddToCart(1, index, false)}
            >
              {t('questions', 'ec', 'target', 'addToCart')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddToCartSidebar
